import React, { createElement, useMemo } from "react";
import { useWindowDimensions, Platform } from "react-native";
import RenderHtml, { RenderHTMLProps } from "react-native-render-html";
import { sanitizeHtmlByDescriptionFormat } from "../../../../utils/sanitizeHtml";
import { useIframeAllowedDomains } from "../../../../hooks/useIframeAllowedDomains";

interface CustomHtmlProps extends Omit<Partial<RenderHTMLProps>, "baseStyle"> {
  html: string;
  style?: RenderHTMLProps["baseStyle"];
  /**
   * When true, preserves sanitized iframe/embed content, such as YouTube, Vimeo,
   * and CodePen embeds.
   * When false, iframe/embed/object/param tags are stripped from the sanitized HTML
   * before rendering on all platforms.
   * On web, content with embeds or list markup is rendered via `dangerouslySetInnerHTML`
   * to preserve embed behavior and list bullet formatting.
   */
  allowEmbeds?: boolean;
}

const EMBED_TAG_PATTERNS = [
  /<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi,
  /<embed\b[^>]*\/?>/gi,
  /<object\b[^>]*>[\s\S]*?<\/object>/gi,
  /<param\b[^>]*\/?>/gi,
];
const LIST_TAG_PATTERN = /<(ul|ol)\b/i;

export const stripEmbedsFromHtml = (html: string): string => {
  if (!html) {
    return html;
  }

  return EMBED_TAG_PATTERNS.reduce((sanitizedHtml, pattern) => sanitizedHtml.replace(pattern, ""), html);
};

export const containsListMarkup = (html: string): boolean => LIST_TAG_PATTERN.test(html);

const CustomHtml: React.FC<CustomHtmlProps> = ({ html, allowEmbeds = false, style, ...props }) => {
  const { width } = useWindowDimensions();
  const iframeDomainAllowlist = useIframeAllowedDomains();

  const sanitizedHtml = useMemo(
    () => sanitizeHtmlByDescriptionFormat("HTML", html, iframeDomainAllowlist) ?? "",
    [html, iframeDomainAllowlist]
  );

  const htmlForRender = useMemo(
    () => (allowEmbeds ? sanitizedHtml : stripEmbedsFromHtml(sanitizedHtml)),
    [allowEmbeds, sanitizedHtml]
  );

  const shouldRenderWebHtmlDirectly =
    Platform.OS === "web" && Boolean(html) && (allowEmbeds || containsListMarkup(htmlForRender));

  if (shouldRenderWebHtmlDirectly) {
    return createElement("div", {
      "data-testid": allowEmbeds ? "custom-html-with-embeds" : "custom-html-list",
      dangerouslySetInnerHTML: { __html: htmlForRender },
      style: {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 16,
        lineHeight: "24px",
        color: "#344054",
        ...(style ?? {}),
      },
    });
  }

  return (
    <RenderHtml
      contentWidth={width}
      source={{ html: htmlForRender }}
      tagsStyles={customStyles}
      baseStyle={style}
      {...props}
    />
  );
};

const customStyles = {
  body: {
    fontFamily: "Inter",
    fontSize: 16,
    color: "#344054",
    lineHeight: 24,
    paragraphSpacing: 16,
  },
  h1: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#344054",
  },
  p: {
    fontFamily: "Inter",
    fontSize: 16,
    lineHeight: 24,
    color: "#344054",
    paragraphSpacing: 16,
  },
  a: {
    color: "#1e90ff",
    textDecorationLine: "underline",
  },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: 14,
    padding: 2,
    paddingRight: 4,
    borderRadius: 4,
    color: "#111827",
    backgroundColor: "#f3f4f6",
  },
  pre: {
    backgroundColor: "#0b1220",
    color: "#e5e7eb",
    borderRadius: 8,
    padding: 12,
  },
  "pre code": {
    color: "#e5e7eb",
    backgroundColor: "transparent",
    padding: 0,
  },

  blockquote: {
    fontStyle: "italic",
    backgroundColor: "#f8fafc",
    borderColor: "#3b82f6",
    borderLeftWidth: 4,
    marginHorizontal: 10,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  "blockquote p": {
    margin: 0,
    fontStyle: "italic",
  },
} as const;

export default CustomHtml;
